import React, { useMemo, useState } from 'react';
import { X, Mail, Loader2, Send, Paperclip, CheckCircle2, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { availableDocs, buildAttachments } from '../../utils/paperworkPdfs';
import { buildEmailLogoHTML } from '../../utils/emailLogo';
import { authedFetch } from '../../utils/staffAuth';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const buildBody = (policy, selectedLabels) => {
  const customer = policy.customer || {};
  const firstName = (customer.firstName || customer.name || 'there').split(' ')[0];
  const list = selectedLabels.map((l) => `<li style="margin:4px 0;">${l}</li>`).join('');
  return `<!doctype html><html><body style="font-family:'Helvetica Neue',Arial,sans-serif;background:#FFF6EB;padding:24px 0;margin:0;color:#1F0068;">
<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"><tr><td align="center">
<table role="presentation" width="560" cellspacing="0" cellpadding="0" border="0" style="background:#fff;border-radius:20px;overflow:hidden;box-shadow:0 8px 30px rgba(31,0,104,0.08);">
<tr><td style="padding:24px 28px;">
${buildEmailLogoHTML()}
<h1 style="color:#1F0068;font-size:22px;margin:24px 0 8px;font-family:'Helvetica Neue',Arial,sans-serif;">Your policy paperwork</h1>
<p style="font-size:14px;line-height:1.55;">Hi ${firstName},</p>
<p style="font-size:14px;line-height:1.55;">Please find the following documents for policy <b>${policy.planNumber}</b> attached to this email:</p>
<ul style="font-size:14px;line-height:1.6;padding-left:20px;">${list}</ul>
<p style="font-size:14px;line-height:1.55;">If anything looks off or you have questions, just reply to this email — we're here to help.</p>
<p style="font-size:14px;line-height:1.55;margin-top:24px;">Kind regards,<br/><b>The Domestic Home Services team</b></p>
</td></tr>
<tr><td style="padding:12px 28px 24px 28px;font-size:11px;color:rgba(31,0,104,0.6);border-top:1px solid rgba(31,0,104,0.06);">
&copy; ${new Date().getFullYear()} Domestic Home Services Limited &middot; Company No. 17368184 &middot; 128 City Road, London, EC1V 2NX. We are committed to providing transparent, professional and customer-focused home maintenance services in accordance with applicable UK laws and consumer protection legislation.
</td></tr>
</table></td></tr></table></body></html>`;
};

const SendPaperworkDialog = ({ open, onClose, policy }) => {
  const { toast } = useToast();
  const customerEmail = policy?.customer?.email || '';
  const isCancelled = policy?.status === 'Cancelled' || !!policy?.cancellation;

  const available = useMemo(() => availableDocs(isCancelled), [isCancelled]);

  const [to, setTo] = useState(customerEmail);
  const [subject, setSubject] = useState(`Your Domestic Home Services paperwork — Policy ${policy?.planNumber || ''}`);
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState(() => new Set(['welcome', 'certificate', 'invoice']));
  const [busy, setBusy] = useState(false);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);

  React.useEffect(() => {
    if (open) {
      setTo(policy?.customer?.email || '');
      setCc('');
      setBcc('');
      setShowCc(false);
      setSubject(`Your Domestic Home Services paperwork — Policy ${policy?.planNumber || ''}`);
      setSelected(new Set(['welcome', 'certificate', 'invoice']));
    }
  }, [open, policy]);

  if (!open || !policy) return null;

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const parseAddrs = (s) => (s || '').split(/[,;\n]/).map((x) => x.trim().toLowerCase()).filter(Boolean);
  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const send = async () => {
    const recipient = to.trim().toLowerCase();
    if (!isValidEmail(recipient)) return toast({ title: 'Please enter a valid recipient email' });
    const ccList = parseAddrs(cc);
    const bccList = parseAddrs(bcc);
    const badCc = ccList.find((e) => !isValidEmail(e));
    if (badCc) return toast({ title: `CC "${badCc}" is not a valid email` });
    const badBcc = bccList.find((e) => !isValidEmail(e));
    if (badBcc) return toast({ title: `BCC "${badBcc}" is not a valid email` });
    if (selected.size === 0) return toast({ title: 'Select at least one document to attach' });

    setBusy(true);
    try {
      const { attachments, chosen, totalBytes } = await buildAttachments([...selected], policy.planNumber, isCancelled);
      if (attachments.length === 0) throw new Error('Could not generate any paperwork PDFs. Are the documents visible on this page?');

      const html = buildBody(policy, chosen.map((c) => c.label)) + (message.trim() ? `<div style="max-width:560px;margin:12px auto;padding:14px 20px;background:#fff;border-radius:14px;color:#1F0068;font-size:14px;line-height:1.5;">${message.replace(/\n/g, '<br/>')}</div>` : '');

      const result = await authedFetch(`${BACKEND}/api/emails/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: recipient,
          cc: ccList.length ? ccList : undefined,
          bcc: bccList.length ? bccList : undefined,
          subject: subject.trim() || 'Your Domestic Home Services paperwork',
          html,
          text: `Please find your Domestic Home Services paperwork attached for policy ${policy.planNumber}.\n\nDocuments: ${chosen.map((c) => c.label).join(', ')}.\n\n${message}`,
          tag: 'paperwork',
          attachments: attachments.map(({ size, ...a }) => a),
        }),
      }, toast);
      if (!result) return; // 401 already redirected to login
      const { res, json: j } = result;
      if (!res.ok || j?.status === 'failed') {
        toast({ title: 'Send failed', description: j?.detail || j?.error || 'Please try again in a moment.' });
      } else if (j?.status === 'queued') {
        toast({ title: 'Queued for send', description: 'Resend is not configured yet — email queued for later delivery.' });
        onClose(true);
      } else {
        toast({ title: 'Paperwork sent', description: `${attachments.length} document${attachments.length === 1 ? '' : 's'} (${(totalBytes / 1024 / 1024).toFixed(2)} MB) delivered to ${recipient}.` });
        onClose(true);
      }
    } catch (e) {
      toast({ title: 'Could not send paperwork', description: String(e?.message || e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="send-paperwork-dialog" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-start justify-between p-6 border-b border-[#1F0068]/5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF5C39] font-bold">Send by email</div>
            <h2 className="text-xl font-bold text-[#1F0068] mt-1 inline-flex items-center gap-2"><Paperclip className="w-4 h-4" /> Email policy paperwork</h2>
            <p className="text-xs text-[#1F0068]/70 mt-1">Delivered live via Resend from <b>hello@domestichomeservices.com</b>.</p>
          </div>
          <button type="button" onClick={() => !busy && onClose()} className="text-[#1F0068]/60 hover:text-[#1F0068]"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[#1F0068] font-semibold">Recipient email</Label>
              {!showCc && (
                <button type="button" onClick={() => setShowCc(true)} data-testid="paperwork-add-cc-bcc" className="text-xs text-[#FF5C39] font-semibold underline">+ Add CC / BCC</button>
              )}
            </div>
            <div className="relative mt-2">
              <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#1F0068]/50" />
              <Input data-testid="paperwork-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" className="h-11 pl-10 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
            </div>
          </div>
          {showCc && (
            <>
              <div>
                <Label className="text-[#1F0068] font-semibold text-xs">CC <span className="text-[#1F0068]/50 font-normal">(comma-separated)</span></Label>
                <Input data-testid="paperwork-cc" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="partner@example.com, landlord@example.com" className="h-10 mt-2 rounded-xl bg-white border-[#1F0068]/10" />
              </div>
              <div>
                <Label className="text-[#1F0068] font-semibold text-xs">BCC <span className="text-[#1F0068]/50 font-normal">(hidden from recipients)</span></Label>
                <Input data-testid="paperwork-bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="records@yourcompany.com" className="h-10 mt-2 rounded-xl bg-white border-[#1F0068]/10" />
              </div>
            </>
          )}
          <div>
            <Label className="text-[#1F0068] font-semibold">Subject</Label>
            <Input data-testid="paperwork-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[#1F0068] font-semibold">Attach paperwork <span className="text-[#1F0068]/50 font-normal">({selected.size} selected)</span></Label>
              <button type="button" onClick={() => setSelected(new Set(available.map((d) => d.key)))} className="text-xs text-[#FF5C39] font-semibold underline" data-testid="paperwork-select-all">Select all</button>
            </div>
            <div className="mt-2 space-y-1.5">
              {available.map((doc) => {
                const checked = selected.has(doc.key);
                return (
                  <label key={doc.key} data-testid={`paperwork-opt-${doc.key}`} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${checked ? 'bg-[#FFEDD6] border-[#FF5C39]' : 'bg-white border-[#1F0068]/10 hover:border-[#1F0068]/25'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(doc.key)} className="w-4 h-4 accent-[#FF5C39]" />
                    <FileText className="w-4 h-4 text-[#1F0068]/60" />
                    <span className="text-sm font-semibold text-[#1F0068] flex-1">{doc.label}</span>
                    {checked && <CheckCircle2 className="w-4 h-4 text-[#FF5C39]" />}
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-[#1F0068] font-semibold">Add a message <span className="text-[#1F0068]/50 font-normal">(optional)</span></Label>
            <Textarea data-testid="paperwork-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="A personal note that will appear above the document list…" className="mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10" />
          </div>
        </div>

        <div className="p-6 border-t border-[#1F0068]/5 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onClose()} disabled={busy} className="rounded-full">Cancel</Button>
          <Button data-testid="paperwork-send" onClick={send} disabled={busy} className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white gap-1.5">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing PDFs & sending…</> : <><Send className="w-4 h-4" /> Send {selected.size} document{selected.size === 1 ? '' : 's'}</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendPaperworkDialog;
